/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import IconUpload from './assets/icon-upload.svg';
import Dropzone from 'react-dropzone';
import UploadItem from './new-upload-item';
import './upload.scss';
import { ListFile, ListDropzone } from './types-uploader';
import Error from 'components/commons/error';
import { DocumentUploadS3 } from 'generated/credit-analysis-graphql';

export type DocumentUploaderProps = {
    title?: string;
    className?: string;
    filesLimit?: number;
    submit?: boolean;
    loading?: boolean;
    id?: string;
    getSignedUrl: (filename: string, contentType: string) => Promise<DocumentUploadS3>;
    onUploadComplete:() => void;
    documentDescription?:() => JSX.Element;
    onError:() => void;
    enableSubmit: (status: boolean) => void;
};

const acceptedContentTypes = [
    'application/pdf',
];

const DocumentUploader: React.FC<DocumentUploaderProps> = ({
    title = 'anexar arquivo',
    className,
    filesLimit,
    submit,
    getSignedUrl,
    onUploadComplete,
    documentDescription,
    onError,
    enableSubmit,
}) => {
    const [documents, setDocuments] = useState<File[]>([]);
    const [filesDropzone, setFilesDropzone] = useState<ListDropzone>([]);
    const [uploadItens, setUploadItens] = useState(false);
    const [hasItem, setHasItem] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    const isValidType = (file: File) => {
        const isValid = acceptedContentTypes.includes(file.type);
        if (!isValid) {
            setErrorMessages([...errorMessages, `O arquivo ${file.name} não foi anexado pois não aceitamos esse formato.`]);
        }

        return isValid;
    }

    const isValidFileSize = (file: File) => {
        const limitInMB = 20;
        const size = Math.round(file.size / 1024 / 1024);
        const isValid = size <= limitInMB;

        if (!isValid) {
            setErrorMessages([...errorMessages, `Limite para tamanho de arquivo excedido: ${limitInMB}MB`]);
        }

        return isValid;
    }

    useEffect(() => {
        setUploadItens(!uploadItens);
        setErrorMessages([]);
    }, [submit]);

    useEffect(() => {
        enableSubmit(hasItem || filesDropzone.length > 0);
    }, [hasItem, filesDropzone]);


    /**
     * Função chamada ao anexar um arquivo no Dropzone.
     * @param {File[]} files - arquivo carregado no input
     */
    const handleUpload = (files: File[]) => {
        const filteredFile = files.filter(isValidFileSize).filter(isValidType);
        if (filesLimit) {
            const isTooManyDraggedFiles = files.length > filesLimit;
            const isOverflow = (documents.length + files.length) > filesLimit;

            if (isTooManyDraggedFiles || isLimitReached() || isOverflow || (files.length + filesDropzone.length) > 2) {
                setErrorMessages([...errorMessages, `Limite de ${filesLimit} arquivo(s) excedido`]);
                return;
            }
        }

        if (filteredFile.length) {
            const newFiles = filteredFile.map(f => {
                return {
                    name: f.name,
                    src: URL.createObjectURL(f),
                    type: f.type,
                    file: f,
                }
            });
            setFilesDropzone([
                ...filesDropzone,
                ...newFiles
            ]);
        }
    }

    /**
     * função chamada quando não é possiver enviar o arquivo anexado.
     * @param {ListFile} file - lista de arquivos enviados com sucesso ou não
     */
    const handleUploadError = (file: ListFile) => {
        // caso ao menos um arquivo tenha sido enviado o botão de salvar é liberado
        if (file.find(f => f.error === false)) {
            setHasItem(true);
        }
        onError();
    }

    const isLimitReached = () => filesLimit && documents.length === filesLimit;

    const limitLabel = () => {
        const fileLabel = filesLimit === 1 ? 'limite de 1 arquivo' : `limite de ${filesLimit} arquivos`;
        const addedLabel = documents.length === 1 ? '1 adicionado' : `${documents.length} adicionados`;
        return `${fileLabel} - ${addedLabel}`;
    };

    return (
        <>
            <div className={className}>
                <Dropzone
                    accept={acceptedContentTypes}
                    onDropAccepted={handleUpload}
                    multiple
                    disabled={documents.length === filesLimit} >
                    {({ getRootProps, getInputProps }) => {
                        return (
                            <div
                                {...getRootProps()}
                                className={`area__upload ${isLimitReached() ? 'disabled' : ''} container__upload`}
                                title={isLimitReached() ? `Limite de ${filesLimit} arquivo(s) atingido` : ''}
                            >
                                <div>
                                    <img src={IconUpload} alt="icon__upload" className="icon__upload" />
                                    <div className="subtitle">{title}</div>
                                    <label className="overline">apenas PDF com até 20 MB</label>
                                    {filesLimit && (
                                        <div className="overline mt-2">{limitLabel()}</div>
                                    )}
                                </div>
                                <input {...getInputProps()} />
                            </div>
                        )
                    }}
                </Dropzone>

                {documentDescription && (
                    <div className="mt-4 mb-4">
                        {documentDescription()}
                    </div>
                )}

                <div className="mb-4">
                    {errorMessages.map((error, index) => <Error key={index + error} message={error} />)}
                    <UploadItem
                        getSignedUrl={getSignedUrl}
                        onComplete={() => {
                            setFilesDropzone([]);
                            onUploadComplete();
                        }}
                        onError={(error) => {
                            handleUploadError(error);
                        }}
                        filesDropzone={filesDropzone}
                        submit={uploadItens}
                        removeDropZone={(src) => setFilesDropzone(filesDropzone.filter(f => f.src !== src))}
                    />
                </div>
            </div>
        </>
    );
};

export default DocumentUploader;
